/**
 * ProductName — the house standard for showing a product: logo tile in the product's colour +
 * "ARVETIO <name>" with "for Microsoft 365" on a second, smaller line.
 *
 * <ProductName product="access-audit" size="md" />
 * <ProductName product="access-governance" size="sm" />
 *
 * Keep identical copies in m365aa-portal (src/components) and m365aa-admin (src).
 * Palettes live in ArvetioLogo.jsx (accessaudit = violet, governance = blue).
 */
import ArvetioLogo from "./ArvetioLogo.jsx";

export const PRODUCTS = {
  "access-audit": { variant: "accessaudit", name: "Access Audit" },
  "access-governance": { variant: "governance", name: "Access Governance" },
};

/** Portal cart/catalogue product ids → ProductName keys. */
export const PRODUCT_BY_ID = {
  "m365-access-audit": "access-audit",
  "access-governance": "access-governance",
};

/** ProductName key for a licence plan id ("standard" → Access Audit, "governance" → Access Governance). */
export function productKeyForPlan(plan) {
  return plan === "governance" ? "access-governance" : "access-audit";
}

/** ProductName key for a cart/catalogue product id (unknown → Access Audit). */
export function productKeyFor(id) {
  return PRODUCT_BY_ID[id] ?? "access-audit";
}

const SIZES = {
  sm: { logo: 30, l1: 15, l2: 11.5, gap: 10 },
  md: { logo: 42, l1: 19, l2: 13, gap: 12 },
  lg: { logo: 54, l1: 24, l2: 15, gap: 14 },
};

export default function ProductName({ product = "access-audit", size = "md", className = "" }) {
  const p = PRODUCTS[product] ?? PRODUCTS["access-audit"];
  const s = SIZES[size] ?? SIZES.md;
  return (
    <span
      className={`product-name ${className}`.trim()}
      style={{ display: "inline-flex", alignItems: "center", gap: s.gap, verticalAlign: "middle" }}
    >
      <ArvetioLogo variant={p.variant} size={s.logo} />
      <span style={{ display: "inline-flex", flexDirection: "column", lineHeight: 1.15, textAlign: "left" }}>
        <span style={{ fontSize: s.l1, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.01em" }}>
          <span style={{ letterSpacing: "0.04em" }}>ARVETIO</span> {p.name}
        </span>
        <span style={{ fontSize: s.l2, fontWeight: 500, color: "var(--text-muted)", marginTop: 2 }}>for Microsoft 365</span>
      </span>
    </span>
  );
}
